import { CommissionStatementResult } from '@api/Entities/CommissionStatement/CommissionStatementResult';
import { Utilities } from '@api/index';
import { COMMISSION_STATEMENT_MODULE_TYPES, CommissionStatementModuleMode, CommissionStatementModuleName } from '@api/Model/CommissionStatement/CommissionStatementModule';
import { CommissionStatementProcessingResult } from '@front/Components/CommissionStatement/CommissionStatementProcessingResult';
import * as React from 'react';
import { CommissionStatement } from '../../MedicareProAPI/Entities/CommissionStatement/CommissionStatement';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import {
  CommissionStatementRepository,
  CommissionStatementStatus
} from '../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementRepository';
import { UserContext } from '../Context/UserContext';


export interface AgentCommissionStatementModulesPanelProps {
  user: User;
}

export default function AgentCommissionStatementModulesPanel(
  props: AgentCommissionStatementModulesPanelProps
): React.JSX.Element {
  // Context and user data
  const userData = React.useContext(UserContext);

  // Selected Module
  const [selectedModule, setSelectedModule] =
    React.useState<CommissionStatementModuleName>(CommissionStatementModuleName.ApplyAndBackfill);

  // Commission statements state
  const [commissionStatements, setCommissionStatements] =
    React.useState<Array<CommissionStatement>>();
  const [commissionStatementsLoading, setCommissionStatementsLoading] =
    React.useState<boolean>(true);
  const [selectedCommissionStatements, setSelectedCommissionStatements] =
    React.useState<Array<number>>([]);

  // Filter state for year and month selection
  const [filterYear, setFilterYear] = React.useState<number | undefined>(
    Number(new Date().getFullYear())
  );
  const [filterMonth, setFilterMonth] = React.useState<number | undefined>(
    Number(new Date().getMonth()) + 1
  );

  // Repository instance for API calls
  const commissionStatementRepository = new CommissionStatementRepository();
  commissionStatementRepository.setAccessToken(userData.userAccessToken);

  // Processing
  const [processMode, setProcessMode] = React.useState<CommissionStatementModuleMode>();
  const [processName, setProcessName] = React.useState<CommissionStatementModuleName>();
  const [processLoading, setProcessLoading] = React.useState<boolean>(false);
  const [runProcessShow, setRunProcessShow] = React.useState<boolean>(false);
  const [commissionStatementResults, setCommissionStatementResults] =
    React.useState<Array<CommissionStatementResult>>([]);


  /**
   * Builds a date range for filtering based on year and optional month
   *
   * @param filterYear - The year to filter by
   * @param filterMonth - Optional month to filter by
   * @returns Object containing from and to date strings
   */
  function buildDateRange(filterYear: number, filterMonth?: number) {
    let from: string;
    let to: string;

    if (filterMonth) {
      // Ensure month is 2 digits
      const month = filterMonth.toString().padStart(2, '0');

      // First day of month
      from = `${filterYear}-${month}-01`;

      // Last day of month — use JS date trick to get the last day
      const lastDay = new Date(filterYear, filterMonth, 0).getDate();
      to = `${filterYear}-${month}-${lastDay}`;
    } else {
      from = `${filterYear}-01-01`;
      to = `${filterYear}-12-31`;
    }

    return { from, to };
  }

  /**
   * Fetches commission statements from the API with optional filtering
   *
   */
  const getCommissionStatements = (): void => {
    const options = {
      criteria: {
        date: buildDateRange(filterYear, filterMonth),
        status: CommissionStatementStatus.Completed
      },
    };

    commissionStatementRepository
      .getCommissionStatements(userData.userId, options)
      .then((commissionStatements) => {
        setCommissionStatements(commissionStatements.getItems());
        setCommissionStatementsLoading(false);
      });
  };

  /**
   * Effect hook to reload commission statements when filters change
   */
  React.useEffect(() => {
    getCommissionStatements();
  }, [filterMonth, filterYear]);

  const selectModule = (module: CommissionStatementModuleName) => {
    setSelectedModule(module);
  };

  /**
   * Handles year filter selection change
   *
   * @param event - The select change event
   */
  const handleChangeFilterYear = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    setFilterYear(Number(selectedValue));
  };

  /**
   * Handles month filter selection change
   *
   * @param event - The select change event
   */
  const handleChangeFilterMonth = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    setFilterMonth(Number(selectedValue));
  };

  const toggleStatement = (statement: CommissionStatement) => {
    const statementId = statement.getId();
    let updatedSelectedStatements = [...selectedCommissionStatements];

    if (updatedSelectedStatements.includes(statementId)) {
      // Deselect statement
      updatedSelectedStatements = updatedSelectedStatements.filter(id => id !== statementId);
    } else {
      // Select statement
      updatedSelectedStatements.push(statementId);
    }

    setSelectedCommissionStatements(updatedSelectedStatements);
  };


  const startExecute = (
    selectedCommissionStatements: Array<number>,
    moduleType: CommissionStatementModuleName
  ) => {
    runProcess(selectedCommissionStatements, moduleType, 'execute');
  };

  const startPlan = (
    selectedCommissionStatements: Array<number>,
    moduleType: CommissionStatementModuleName
  ) => {
    runProcess(selectedCommissionStatements, moduleType, 'plan');
  };

  const runProcess = async (
    selectedCommissionStatements: Array<number>,
    moduleType: CommissionStatementModuleName,
    mode: CommissionStatementModuleMode = 'plan'
  ) => {
    setProcessLoading(true);
    setRunProcessShow(true);
    setProcessMode(mode);
    setProcessName(moduleType);

    const commissionStatementResponses = [];

    for (const selectedCommissionStatement of selectedCommissionStatements) {
      try {
        const commissionStatementResponse = await commissionStatementRepository.runCommissionStatementModule(
          userData.userId,
          selectedCommissionStatement,
          moduleType,
          mode,
          {
            include: ['commissionStatement']
          }
        );

        commissionStatementResponses.push(commissionStatementResponse);
      } catch (e) {
        break;
      }
    }

    setProcessLoading(false);
    setCommissionStatementResults(commissionStatementResponses);
  };


  const selectedModileDetails = COMMISSION_STATEMENT_MODULE_TYPES.find(module => module.name === selectedModule);

  return (
    <>
      {/* Main Content Card */}
      <div className="card pb-0 pt-0" style={{ height: 'auto' }}>
        <div className="row">

          {/* Statements Content */}
          {commissionStatementsLoading === false && (
            <>
              {/* Left Sidebar - Statement List */}
              <div className="col-12 col-lg-2 col-lg-4 col-xl-2 pt-2">


                {/* Module List */}
                {COMMISSION_STATEMENT_MODULE_TYPES &&
                  COMMISSION_STATEMENT_MODULE_TYPES.map((module) => {
                    // Build CSS classes for card
                    const classList = [
                      'small',
                      'card',
                      'p-2',
                      'mb-2',
                      'card-tall',
                      'border',
                      'border-info',
                      'ml-2',
                    ];

                    const classListInner = [
                      'badge',
                      'small',
                      'float-right',
                      'font-weight-normal',
                      'text-white',
                      'ml-1',
                    ];

                    // Highlight selected statement
                    if (selectedModule && module.name === selectedModule) {
                      classList.push('bg-info');
                      classList.push('text-white');
                    }

                    // Color code by status
                    if (module.available === true) {
                      classList.push('cp');
                      classListInner.push('bg-success');
                    } else {
                      classListInner.push('bg-warning');
                    }

                    return (
                      <div
                        key={module.name}
                        onClick={module.available ? (e) => selectModule(module.name) : undefined}
                        className={classList.join(' ')}
                      >
                        <div>
                          <span className={classListInner.join(' ')}>
                            {module.available ? 'Available' : 'Coming Soon'}
                          </span>
                          <p className="mb-0">
                            <strong>{module.label}</strong>
                          </p>
                          <p className="mb-0">{module.summary}</p>
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div className="col-12 col-lg-8 col-xl-10">
                <div className="p-2">
                  <h4>{selectedModileDetails.label}</h4>
                  <p>{selectedModileDetails.description}</p>
                  <p className="alert alert-info">Use the form below to select which statements you want to run this module against.</p>
                  <div className="row">
                    {/* Right Side - Module */}
                    <div className="col-12">
                      <div className="row">
                        {/* Commission Statement List */}
                        <div className="col-6 col-xl-4">
                          {/* Filter Controls */}
                          <div className="row">
                            <div className="col-6">
                              <select
                                onChange={handleChangeFilterYear}
                                className="form-control form-control-sm mb-2"
                                value={filterYear}
                              >
                                <option>Statement Year</option>
                                {Utilities.getYears(15).map((year) => {
                                  return (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            <div className="col-6">
                              <select
                                disabled={!filterYear}
                                onChange={handleChangeFilterMonth}
                                className="form-control form-control-sm mb-2"
                                value={filterMonth}
                              >
                                <option>All Months</option>
                                {Utilities.getMonths().map((month, index) => {
                                  return (
                                    <option key={index} value={index + 1}>
                                      {month}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            <div className="col-12">
                              {/* Empty State */}
                              {commissionStatements.length === 0 && (
                                <p className="alert alert-info small ml-2 mt-0">No statements to display</p>
                              )}

                              {/* Statement List */}
                              {commissionStatements &&
                                commissionStatements.map((statement) => {
                                  // Build CSS classes for statement card
                                  const classList = [
                                    'small',
                                    'card',
                                    'p-2',
                                    'mb-2',
                                    'cp',
                                    'card-tall',
                                    'border',
                                    'border-info',
                                  ];
                                  const classListInner = [
                                    'badge',
                                    'small',
                                    'float-right',
                                    'font-weight-normal',
                                    'text-white',
                                    'ml-1',
                                  ];

                                  // Highlight selected statement
                                  if (selectedCommissionStatements.includes(statement.getId())) {
                                    classList.push('bg-success');
                                    classList.push('text-white');
                                  }

                                  return (
                                    <div
                                      key={statement.getId()}
                                      onClick={(e) => toggleStatement(statement)}
                                      className={classList.join(' ')}
                                    >
                                      <div>
                                        <p className="mb-0">
                                          <strong>{statement.getName()}</strong>
                                        </p>
                                        <p className="mb-0">{statement.getFileName()}</p>
                                      </div>
                                    </div>
                                  );
                                })}

                              <button
                                disabled={selectedCommissionStatements.length === 0}
                                className="btn btn-success btn-sm mb-2"
                                onClick={(e) => startPlan(selectedCommissionStatements, selectedModileDetails.name)}
                              >
                                Continue
                              </button>

                            </div>
                          </div>
                        </div>
                        {/* Commission Statement Results */}
                        <div className="col-6 col-xl-8">
                          {processLoading === true && (
                            <p className="alert alert-info small m-2">
                              Loading... <i className="fas fa-spinner fa-spin"></i>
                            </p>
                          )}
                          {(runProcessShow && commissionStatementResults.length > 0) && (
                            commissionStatementResults.map((result) => (<>
                              <h5>{result.getCommissionStatement().getName()}</h5>
                              <div className="mb-4">
                                <CommissionStatementProcessingResult
                                  key={result.getId()}
                                  module={result.getModule()}
                                  data={result.getData()}
                                  mode="plan"
                                />
                              </div>
                            </>))
                          )}
                        </div>
                      </div>
                    </div>

                  </div>

                </div>


              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
